import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @param {string} text
 * @returns {any | null}
 */
function parseCartJsonResponse(text) {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    console.error('[CartItemsComponent] Expected JSON cart response, received:', trimmed.slice(0, 160));
    return null;
  }

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    console.error('[CartItemsComponent] Invalid JSON cart response:', error);
    return null;
  }
}

/**
 * @returns {string}
 */
function cartJsonUrl() {
  const url = Theme.routes.cart_url;
  return url.endsWith('.js') ? url : `${url}.js`;
}

/**
 * @returns {string}
 */
function cartChangeJsonUrl() {
  const url = Theme.routes.cart_change_url;
  return url.endsWith('.js') ? url : `${url}.js`;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #enforceQtySubscriptionInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#enforceQtySubscriptionRules();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // WebKit doesn't flush pending styles in getAnimations(), so without this reflow
      // onAnimationEnd can see no animation and delete the row while the click event is
      // still dispatching (which made the cart drawer close itself on iOS).
      void row.offsetWidth;

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * @returns {{ productId: number, minQty: number } | null}
   */
  #getSubUpsellQtyRules() {
    if (this.dataset.subUpsellRulesEnabled !== 'true') return null;

    const productId = Number(this.dataset.subUpsellQtyProductId);
    const minQty = Number(this.dataset.subUpsellQtyMin) || 6;

    if (!productId) return null;

    return { productId, minQty };
  }

  /**
   * @param {number} line
   * @param {number} quantity
   * @returns {boolean}
   */
  #shouldStripSubscriptionForQty(line, quantity) {
    const rules = this.#getSubUpsellQtyRules();
    if (!rules) return false;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return false;

    const productId = Number(lineItemRow.dataset.productId);
    if (productId !== rules.productId) return false;
    if (quantity < rules.minQty) return false;

    return lineItemRow.dataset.hasSellingPlan === 'true';
  }

  /**
   * @returns {Set<string>}
   */
  #getSectionIdsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /**
   * @param {Object} parsedResponseText
   * @param {number} line
   */
  #processCartChangeResponse(parsedResponseText, line) {
    resetShimmer(this);

    if (!parsedResponseText || parsedResponseText.errors) {
      if (parsedResponseText?.errors) {
        this.#handleCartError(line, parsedResponseText);
      }
      return;
    }

    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
    if (!sectionHtml) {
      console.error('[CartItemsComponent] Cart change response missing section HTML for', this.sectionId);
      return;
    }

    const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]).catch((error) =>
      console.error('[CartItemsComponent] morph failed, keeping current cart DOM.', error)
    );
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * @returns {number}
   */
  #getCartItemCount() {
    const countEl = this.querySelector('[ref="cartItemCount"]');
    if (!countEl?.textContent) return 0;
    const count = parseInt(countEl.textContent, 10);
    return Number.isFinite(count) ? count : 0;
  }

  /**
   * Removes subscription from configured product lines at or above the qty threshold.
   */
  async #enforceQtySubscriptionRules() {
    const rules = this.#getSubUpsellQtyRules();
    if (!rules || this.#enforceQtySubscriptionInFlight) return;
    if (this.#getCartItemCount() === 0) return;

    this.#enforceQtySubscriptionInFlight = true;

    try {
      const cartResponse = await fetch(cartJsonUrl(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        // Safari can serve a stale cached /cart.js, which makes these rules act on old data
        cache: 'no-store',
      });
      const cart = parseCartJsonResponse(await cartResponse.text());
      if (!cart) return;

      const linesToFix = [];

      cart.items?.forEach((item, index) => {
        if (
          item.product_id === rules.productId &&
          item.quantity >= rules.minQty &&
          item.selling_plan_allocation
        ) {
          linesToFix.push({ line: index + 1, quantity: item.quantity });
        }
      });

      if (!linesToFix.length) return;

      const sectionsToUpdate = this.#getSectionIdsToUpdate();
      let lastResponse = null;
      let lastLine = linesToFix[0].line;

      for (const { line, quantity } of linesToFix) {
        lastLine = line;
        const body = JSON.stringify({
          line,
          quantity,
          selling_plan: '',
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        const response = await fetch(cartChangeJsonUrl(), fetchConfig('json', { body }));
        lastResponse = parseCartJsonResponse(await response.text());

        if (!lastResponse || lastResponse.errors || lastResponse.status) {
          console.error('[CartSubUpsellQtyRule]', lastResponse?.errors || lastResponse);
          return;
        }
      }

      if (lastResponse) {
        this.#processCartChangeResponse(lastResponse, lastLine);
        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              resource: lastResponse,
              sourceId: 'cart-sub-upsell-qty-enforce',
              data: {
                itemCount: lastResponse.item_count || 0,
                source: 'cart-sub-upsell-qty-enforce',
                sections: lastResponse.sections,
              },
            },
          })
        );
      }
    } catch (error) {
      console.error('[CartSubUpsellQtyRule]', error);
    } finally {
      this.#enforceQtySubscriptionInFlight = false;
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    /** @type {Record<string, string | number>} */
    const changeBody = {
      line: line,
      quantity: quantity,
      sections: Array.from(this.#getSectionIdsToUpdate()).join(','),
      sections_url: window.location.pathname,
    };

    if (this.#shouldStripSubscriptionForQty(line, quantity)) {
      changeBody.selling_plan = '';
    }

    const body = JSON.stringify(changeBody);

    cartTotal?.shimmer();

    fetch(cartChangeJsonUrl(), fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = parseCartJsonResponse(responseText);
        if (!parsedResponseText) return;
        this.#processCartChangeResponse(parsedResponseText, line);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .catch((error) => console.error('[CartItemsComponent] section re-render failed, keeping current cart DOM.', error));
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    // Only trust section HTML that actually contains this section's wrapper — a bot-challenge
    // page or truncated response would otherwise make morphSection throw and kill the cart UI.
    if (typeof cartItemsHtml === 'string' && cartItemsHtml.includes(`shopify-section-${this.sectionId}`)) {
      morphSection(this.sectionId, cartItemsHtml)
        .then(() => this.#updateCartQuantitySelectorButtonStates())
        .catch((error) => console.error('[CartItemsComponent] morph failed, keeping current cart DOM.', error));
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .catch((error) => console.error('[CartItemsComponent] section re-render failed, keeping current cart DOM.', error));
    }

    if (event.detail?.data?.source === 'cart-sub-upsell-qty-enforce') return;

    queueMicrotask(() => {
      this.#enforceQtySubscriptionRules();
    });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
